import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import leftArrow from 'open-iconic/svg/arrow-thick-left.svg';

import ComponentButtonsList from 'app/ComponentButtonsList/components/ComponentButtonsList';
import PageButtonsList from 'app/PageButtonsList/container';
import { NUI_SCHEMA_NAMES } from 'lib/contract';
import { EN } from 'lib/locales';
import { validateComponentName } from 'lib/nameValidations';
import { setFailedValidationMessageForField, validateFieldValue } from 'lib/validations';
import { SAVED } from 'lib/versions';
import { IconButton } from 'ui/Components';
import {
  API_COMPONENT_ACTION_ADDED,
  UI_ADD_MASTER_COMPONENT_TO_PAGE,
  componentImageLocation
} from 'utils/factory';

import ExperimentForm from './ExperimentForm';
import GenericForm from './GenericForm';

import styles from './EditorPane.css';

/**
 * the property editor (right sidebar)
 * @param {object} props - props
 * @returns {React.Component} the property editor view
 */
export default class EditorPane extends Component {
  static propTypes = {
    checkComponentNameAvailability: propTypes.func.isRequired,
    clearComponentApiResponse: propTypes.func.isRequired,
    clearSearch: propTypes.func.isRequired,
    component: propTypes.shape(),
    compareAndFetchSizes: propTypes.func.isRequired,
    contractPreview: propTypes.shape(),
    copyComponent: propTypes.func.isRequired,
    editStatus: propTypes.string.isRequired,
    editorPane: propTypes.object,
    experimentParameters: propTypes.array,
    failedValidations: propTypes.object.isRequired,
    getDynamicValues: propTypes.func.isRequired,
    isFavorite: propTypes.bool,
    isFetchingComponent: propTypes.bool,
    isPageSelected: propTypes.bool,
    isReadonly: propTypes.bool,
    modifyComponent: propTypes.func.isRequired,
    notifyFailure: propTypes.func.isRequired,
    openComponentForEditing: propTypes.func.isRequired,
    openParentComponentForEditing: propTypes.func.isRequired,
    pageComponent: propTypes.shape(),
    removeAddedComponentFromPage: propTypes.func.isRequired,
    saveComponent: propTypes.func.isRequired,
    setFailedValidations: propTypes.func.isRequired,
    showPane: propTypes.bool.isRequired,
    shouldRenderToggleButton: propTypes.func.isRequired,
    audienceTags: propTypes.array.isRequired,
    toggleFavoriteContent: propTypes.func.isRequired,
    unboundVisitComponent: propTypes.func.isRequired,
    unpublishComponent: propTypes.func.isRequired,
    validateFieldValue: propTypes.func,
    visitComponent: propTypes.func.isRequired,
    variationMap: propTypes.object.isRequired,
    contractMap: propTypes.object.isRequired,
    isPreview: propTypes.bool
  };

  static defaultProps = {
    component: {},
    contractPreview: null,
    editorPane: {},
    experimentParameters: [],
    isFavorite: false,
    isFetchingComponent: false,
    isPageSelected: false,
    isReadonly: true,
    isPreview: false,
    pageComponent: {},
    validateFieldValue
  };

  constructor(props) {
    super(props);

    this.onModifyComponent = this.onModifyComponent.bind(this);
  }

  async componentDidUpdate(prevProps) {
    const { component, compareAndFetchSizes, contractMap } = this.props;
    const prevList = componentImageLocation(prevProps.component, contractMap);
    const newList = componentImageLocation(component, contractMap);
    if (!isEqual(sortBy(newList), sortBy(prevList))) {
      await compareAndFetchSizes(component.Id, newList);
    }
  }

  /**
   * returns bound action to modify component with the components Id
   * @param {string} value - new value
   * @param {string} location - value to be modified at object location
   * @param {object} fieldContract - contract of the field
   * @returns {undefined}
   */
  onModifyComponent(value, location, fieldContract) {
    const {
      component,
      failedValidations,
      modifyComponent,
      setFailedValidations,
      validateFieldValue
    } = this.props;

    if (fieldContract) {
      const validationMessage = validateFieldValue(value, fieldContract);
      const newFailedValidations = setFailedValidationMessageForField(
        component.Id,
        location,
        validationMessage,
        failedValidations
      );
      setFailedValidations(newFailedValidations);
    }

    modifyComponent(value, location, component.Id);
  }

  /**
   * get whether form should render as readonly
   * @returns {boolean} readonly
   */
  get isReadonly() {
    const {
      component: { uiAction },
      isReadonly
    } = this.props;

    return isReadonly || uiAction === UI_ADD_MASTER_COMPONENT_TO_PAGE;
  }

  /**
   * get contract of current component
   * @returns {object} a contract
   */
  get currentContract() {
    if (this.props.contractPreview) {
      return this.props.contractPreview;
    }
    const variation = Object.prototype.hasOwnProperty.call(
      this.props.variationMap,
      this.currentVariation
    )
      ? this.props.variationMap[this.currentVariation]
      : this.currentVariation;
    return this.props.contractMap[variation];
  }

  /**
   * get schema name of current component
   * @returns {string} schema name
   */
  get currentSchemaName() {
    const { component: { SchemaName } = {} } = this.props;
    return SchemaName;
  }

  /**
   * get variation of current component
   * @returns {string} variation
   */
  get currentVariation() {
    const { component: { Variation } = {} } = this.props;
    return Variation || this.currentSchemaName;
  }

  /**
   * render form generically or for experiments
   * @returns {React.Component} component form
   */
  renderForm() {
    const {
      checkComponentNameAvailability,
      clearSearch,
      component,
      editorPane,
      experimentParameters,
      failedValidations,
      getDynamicValues,
      isFavorite,
      isFetchingComponent,
      isPageSelected,
      notifyFailure,
      openComponentForEditing,
      setFailedValidations,
      audienceTags,
      toggleFavoriteContent,
      visitComponent,
      isPreview
    } = this.props;

    /**
     * returns bound action to open the component for editing
     * @param {object} childComponent - component to be opened in editor pane
     * @returns {object} OPEN_COMPONENT_FOR_EDITING action
     */
    const boundOpenComponentForEditing = childComponent =>
      openComponentForEditing(childComponent, component);

    let Form;
    let formProps = {
      componentDetails: component,
      isFavorite,
      isNewComponent: component.Action === API_COMPONENT_ACTION_ADDED,
      isReadonly: this.isReadonly,
      audienceTags,

      // key based on component id so things "reset" when components change
      key: component.Id,
      notifyFailure,
      onNameChange: componentName => {
        const validationMessage = validateComponentName(componentName, component.IsDynamic);
        const newFailedValidations = setFailedValidationMessageForField(
          component.Id,
          'ComponentName',
          validationMessage,
          failedValidations
        );
        setFailedValidations(newFailedValidations);
        if (!validationMessage) {
          checkComponentNameAvailability(component.Id, componentName);
        }
      },

      toggleFavoriteContent: () =>
        toggleFavoriteContent(
          isFavorite,
          component.ComponentName,
          'component',
          component.Id,
          EN
        )
    };

    if (this.currentVariation === NUI_SCHEMA_NAMES.EXPERIMENT) {
      Form = ExperimentForm;
      formProps = {
        ...formProps,
        clearSearch,
        experimentParameters,
        isPageSelected,
        modifyComponent: this.onModifyComponent,
        boundOpenComponentForEditing,
        visitComponent
      };
    } else {
      Form = GenericForm;
      formProps = {
        ...formProps,
        isPreview,
        boundOpenComponentForEditing,
        editorPane,
        getDynamicValues,
        contract: this.currentContract,
        failedValidations: failedValidations[component.Id] || {},
        isFetchingComponent,
        onModifyComponent: this.onModifyComponent
      };
    }

    return <Form {...formProps} />;
  }

  renderCTAs() {
    const {
      clearComponentApiResponse,
      component,
      contractPreview,
      copyComponent,
      editStatus,
      failedValidations,
      isPageSelected,
      isReadonly,
      isPreview,
      pageComponent,
      removeAddedComponentFromPage,
      saveComponent,
      setFailedValidations,
      unboundVisitComponent,
      unpublishComponent,
      visitComponent,
      contractMap
    } = this.props;

    if (contractPreview) {
      return null;
    }
    if (isPageSelected) {
      return <PageButtonsList />;
    }
    return (
      <ComponentButtonsList
        apiResponse={component.apiResponse}
        clearComponentApiResponse={clearComponentApiResponse}
        component={pageComponent}
        copyComponent={copyComponent}
        editStatus={editStatus}
        failedValidations={failedValidations}
        isReadonly={isPreview ? false : isReadonly}
        removeAddedComponentFromPage={() => {
          return removeAddedComponentFromPage(component.ComponentName, component.Id);
        }}
        saveComponent={saveComponent}
        modifyComponent={this.onModifyComponent}
        setFailedValidations={setFailedValidations}
        unboundVisitComponent={unboundVisitComponent}
        unpublishComponent={unpublishComponent}
        visitComponent={visitComponent}
        contractMap={contractMap}
      />
    );
  }

  render() {
    const {
      component,
      isPageSelected,
      openParentComponentForEditing,
      showPane,
      shouldRenderToggleButton
    } = this.props;

    const { parentComponentsStack = [] } = component;
    const doesHaveParentComponent = parentComponentsStack.length > 0;
    const editorPaneBorderStyle = isPageSelected
      ? styles.pageWrapper
      : styles.componentWrapper;

    const togglePaneStyle = showPane ? classnames('pane', styles.open) : styles.closed;

    shouldRenderToggleButton(this.currentVariation);

    if (this.currentVariation) {
      return (
        <Fragment>
          <div className={togglePaneStyle}>
            <div className={editorPaneBorderStyle}>
              {doesHaveParentComponent && (
                <div
                  className={`d-flex align-items-center px-4 py-3 ${styles.backToParentComponent}`}
                  role="presentation"
                  onClick={() => openParentComponentForEditing()}
                  data-test-id="editorPane-containerComponent-backButton"
                >
                  <IconButton icon={leftArrow} noMargin rem={1.5} />
                  <span className="ml-3">
                    {parentComponentsStack[parentComponentsStack.length - 1].ComponentName}
                  </span>
                </div>
              )}
              <div
                title={
                  component.Version === SAVED
                    ? '* indicates latest version of the component is the saved version'
                    : ''
                }
                className="px-4 py-3 font-weight-bold border-top"
              >
                {`${this.currentContract.displayName ||
                  this.currentContract.name} Component Fields `}
                {component.Version === SAVED ? '*' : ''}
              </div>
              <div className={styles.header}>{this.renderForm()}</div>
            </div>
            {this.renderCTAs()}
          </div>
        </Fragment>
      );
    }

    return <div data-test-id="editor-pane-invalid-schema" />;
  }
}
